import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ExternalLink, SkipForward } from 'lucide-react';
import { formatClocks, formatTime } from '@/lib/utils';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const studentLabel = (s) => {
  if (!s) return '—';
  const kz = s.kassenzeichen ? ` (${s.kassenzeichen})` : '';
  return `${s.name || '—'}${kz}`;
};

/** Build alphabetical student queue from courses (+ optional DQ order preference). */
function buildQueue(courses, preferredOrder) {
  const byId = new Map();
  for (const c of courses || []) {
    for (const s of c.students || []) {
      if (!s?.id) continue;
      if (!byId.has(s.id)) {
        byId.set(s.id, { id: s.id, name: s.name, kassenzeichen: s.kassenzeichen });
      }
    }
  }
  const preferred = (preferredOrder || []).filter((s) => byId.has(s.id));
  const preferredIds = new Set(preferred.map((s) => s.id));
  const rest = Array.from(byId.values())
    .filter((s) => !preferredIds.has(s.id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de'));
  // Prefer DQ list order (already alphabetical), then any extras
  const fromPreferred = preferred.map((s) => byId.get(s.id));
  return [...fromPreferred, ...rest];
}

export function TariffAssignDialog({ open, onClose, onAssigned, initialStudents = [] }) {
  const [courses, setCourses] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [tariffId, setTariffId] = useState('');
  const [saving, setSaving] = useState(false);

  const current = queue[index] || null;

  const studentCourses = useMemo(() => {
    if (!current?.id) return [];
    return (courses || []).filter((c) => (c.students || []).some((s) => s.id === current.id));
  }, [courses, current?.id]);

  const load = async () => {
    const [cRes, tRes] = await Promise.all([
      api.get('/courses/without-tariff'),
      api.get('/tariffs-discounts/tariffs'),
    ]);
    const list = cRes.data.courses || [];
    setCourses(list);
    setTariffs((tRes.data || []).filter((t) => t.is_active));
    return list;
  };

  useEffect(() => {
    if (!open) return;
    setTariffId('');
    setIndex(0);
    load()
      .then((list) => {
        const q = buildQueue(list, initialStudents);
        setQueue(q);
        if (!q.length) {
          toast.success('Alle betroffenen Kurse haben einen Tarif');
          onClose();
        }
      })
      .catch(() => toast.error('Kurse ohne Tarif konnten nicht geladen werden'));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-select all courses of the current student
  useEffect(() => {
    setSelected(new Set(studentCourses.map((c) => c.id)));
  }, [current?.id, studentCourses]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const advanceAfterFix = async () => {
    onAssigned?.();
    const list = await load();
    const q = buildQueue(list, initialStudents);
    setQueue(q);
    if (!q.length) {
      toast.success('Alle Schüler ohne Tarif sind erledigt');
      onClose();
      return;
    }
    const stillIdx = current ? q.findIndex((s) => s.id === current.id) : -1;
    if (stillIdx >= 0) {
      // Noch Kurse ohne Tarif → bei diesem Schüler bleiben
      setIndex(stillIdx);
    } else {
      // Erledigt → nächster in der alphabetischen Liste (gleiche Index-Position)
      setIndex(Math.min(index, q.length - 1));
    }
    setTariffId('');
  };

  const skip = () => {
    if (index >= queue.length - 1) {
      onClose();
      return;
    }
    setIndex((i) => i + 1);
    setTariffId('');
  };

  const assign = async () => {
    const ids = Array.from(selected);
    if (!tariffId || ids.length === 0) return;
    setSaving(true);
    try {
      const r = await api.post('/courses/bulk-tariff', {
        course_ids: ids,
        tariff_id: tariffId,
      });
      toast.success(
        `${r.data.updated} Kurs(e) für ${studentLabel(current)} → „${r.data.tariff_name}"`,
      );
      await advanceAfterFix();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zuordnung fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="tariff-assign-dialog">
        <DialogHeader>
          <DialogTitle>
            Tarife zuordnen
            {queue.length > 0 && (
              <span className="ml-2 text-sm font-normal text-slate-500">
                Schüler {Math.min(index + 1, queue.length)} / {queue.length}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {current ? (
          <div className="space-y-4">
            <div
              className="rounded-lg border-2 border-brand-red/30 bg-brand-red/5 px-4 py-3"
              data-testid="tariff-assign-current-student"
            >
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Aktueller Schüler</div>
              <div className="text-lg font-semibold text-slate-900 leading-tight">
                {current.name}
                {current.kassenzeichen ? (
                  <span className="ml-2 text-base font-mono font-normal text-slate-600">
                    {current.kassenzeichen}
                  </span>
                ) : null}
              </div>
              <Link
                to={`/students/${current.id}`}
                className="inline-flex items-center gap-1 text-xs text-brand-red hover:underline mt-1"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="w-3 h-3" /> Schülerakte öffnen
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <select
                value={tariffId}
                onChange={(e) => setTariffId(e.target.value)}
                className="flex-1 h-9 rounded-md border border-slate-300 text-sm px-2"
                data-testid="tariff-assign-select"
              >
                <option value="">Tarif wählen…</option>
                {tariffs.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} — {Number(t.amount).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}/
                    {t.billing_type === 'monthly' ? 'Monat' : t.billing_type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <p className="text-xs text-slate-500 mb-1.5">
                Kurse ohne Tarif für diesen Schüler ({studentCourses.length})
              </p>
              <div className="border border-slate-200 rounded-lg max-h-[40vh] overflow-y-auto">
                <table className="w-full text-xs border-collapse">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="border-b border-slate-200 text-[10px] uppercase text-slate-500">
                      <th className="py-1.5 px-2 w-8" />
                      <th className="text-left py-1.5 px-2 font-medium">Kurs</th>
                      <th className="text-left py-1.5 px-2 font-medium w-32">Lehrkraft</th>
                      <th className="text-left py-1.5 px-2 font-medium w-20">Termin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentCourses.map((c) => (
                      <tr
                        key={c.id}
                        className="border-b border-slate-100 hover:bg-brand-red/5 cursor-pointer"
                        onClick={() => toggle(c.id)}
                        data-testid={`tariff-assign-row-${c.id}`}
                      >
                        <td className="py-1.5 px-2">
                          <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)} />
                        </td>
                        <td className="py-1.5 px-2 font-medium">{formatClocks(c.name) || c.name}</td>
                        <td className="py-1.5 px-2 text-slate-600 truncate">{c.teacher_name || '—'}</td>
                        <td className="py-1.5 px-2 text-slate-600 whitespace-nowrap">
                          {c.day_of_week != null ? WEEKDAYS[c.day_of_week] : ''}{' '}
                          {formatTime(c.start_time) || ''}
                        </td>
                      </tr>
                    ))}
                    {studentCourses.length === 0 && (
                      <tr>
                        <td colSpan={4} className="py-6 text-center text-slate-400 italic">
                          Keine Kurse ohne Tarif für diesen Schüler
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <Button type="button" variant="ghost" size="sm" onClick={skip} data-testid="tariff-assign-skip">
                <SkipForward className="w-3.5 h-3.5 mr-1" /> Überspringen
              </Button>
              <Button
                onClick={assign}
                disabled={!tariffId || selected.size === 0 || saving}
                className="bg-brand-red hover:bg-brand-red/90"
                data-testid="tariff-assign-submit"
              >
                {saving ? 'Speichere…' : `${selected.size} Kurs(e) zuordnen & weiter`}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500 py-8 text-center">Keine Schüler ohne Tarif.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
